import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import { AppSidebar } from '@/components/AppSidebar';
import { AppHeader } from '@/components/AppHeader';
import { Progress } from '@/components/ui/progress';
import { 
  Search, 
  Star, 
  Clock, 
  Users, 
  ArrowUpDown,
  RefreshCw,
  Download,
  MessageCircle,
  ChevronLeft,
  ChevronRight,
  Trophy,
  Target,
  Timer,
  Shuffle,
  Plus,
  XCircle
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface Player {
  id: string;
  name: string;
  position: 'GK' | 'DEF' | 'MID' | 'FWD';
  team: string;
  photo_url?: string;
  is_active: boolean;
  stats?: {
    goals: number;
    assists: number;
    cleanSheets: number;
    points: number;
  };
  status: 'available' | 'drafted' | 'watchlist';
  draftedBy?: string;
}

interface DraftPick {
  round: number;
  pick: number;
  teamId: string;
  teamName: string;
  playerId?: string;
  player?: Player;
}

interface LeagueTeam {
  id: string;
  teamName: string;
  ownerName: string;
  isYou: boolean;
}

interface ChatMessage {
  id: string;
  userId: string;
  username: string;
  message: string;
  timestamp: string;
  isSystem: boolean;
}

const DraftRoom = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  // Draft state
  const [currentRound, setCurrentRound] = useState(1);
  const [currentPick, setCurrentPick] = useState(1);
  const [timeLeft, setTimeLeft] = useState(60);
  const [currentTeam, setCurrentTeam] = useState<LeagueTeam | null>(null);
  const [isYourTurn, setIsYourTurn] = useState(false);
  const [draftComplete, setDraftComplete] = useState(false);
  
  // Data state
  const [players, setPlayers] = useState<Player[]>([]);
  const [draftBoard, setDraftBoard] = useState<DraftPick[]>([]);
  const [teams, setTeams] = useState<LeagueTeam[]>([]);
  const [yourRoster, setYourRoster] = useState<Player[]>([]);
  const [yourQueue, setYourQueue] = useState<Player[]>([]);
  const [watchlist, setWatchlist] = useState<Player[]>([]);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  
  // UI state
  const [searchTerm, setSearchTerm] = useState('');
  const [positionFilter, setPositionFilter] = useState<string>('all');
  const [teamFilter, setTeamFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState('points');
  const [boardView, setBoardView] = useState<'compact' | 'expanded'>('expanded');
  const [chatCollapsed, setChatCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Mock data for development
  const mockPlayers: Player[] = [
    {
      id: '1',
      name: 'Erling Haaland',
      position: 'FWD',
      team: 'Manchester City',
      photo_url: null,
      is_active: true,
      stats: { goals: 36, assists: 8, cleanSheets: 0, points: 272 },
      status: 'available'
    },
    {
      id: '2', 
      name: 'Mohamed Salah',
      position: 'FWD',
      team: 'Liverpool',
      photo_url: null,
      is_active: true,
      stats: { goals: 24, assists: 12, cleanSheets: 0, points: 228 },
      status: 'available'
    },
    {
      id: '3',
      name: 'Kevin De Bruyne',
      position: 'MID',
      team: 'Manchester City',
      photo_url: null,
      is_active: true,
      stats: { goals: 7, assists: 16, cleanSheets: 0, points: 180 },
      status: 'available'
    },
    {
      id: '4',
      name: 'Virgil van Dijk',
      position: 'DEF',
      team: 'Liverpool',
      photo_url: null,
      is_active: true,
      stats: { goals: 3, assists: 2, cleanSheets: 15, points: 158 },
      status: 'available'
    },
    {
      id: '5',
      name: 'Alisson Becker',
      position: 'GK',
      team: 'Liverpool',
      photo_url: null,
      is_active: true,
      stats: { goals: 0, assists: 1, cleanSheets: 18, points: 148 },
      status: 'available'
    }
  ];

  const mockTeams: LeagueTeam[] = [
    { id: '1', teamName: 'Remy\'s Team', ownerName: 'Remy Gogoll', isYou: true },
    { id: '2', teamName: 'Arsenal FC', ownerName: 'John Doe', isYou: false },
    { id: '3', teamName: 'Chelsea Blues', ownerName: 'Jane Smith', isYou: false },
    { id: '4', teamName: 'Liverpool Reds', ownerName: 'Mike Johnson', isYou: false }
  ];

  useEffect(() => {
    if (id) {
      fetchDraftData();
    }
    
    // Listen for draft start broadcasts
    const leagueId = id;
    if (!leagueId) return;

    const channel = supabase.channel(`draft_start_${leagueId}`);
    
    channel
      .on('broadcast', { event: 'draft:start' }, (payload) => {
        console.log('Draft started by broadcast:', payload);
        toast({
          title: 'Draft has started!',
          description: 'The draft has been started by the league owner.',
        });
        // Draft room is already loaded, just ensure proper state
        setDraftComplete(false);
        setCurrentRound(1);
        setCurrentPick(1);
        setTimeLeft(90); // Reset timer
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [id]);

  // Timer effect
  useEffect(() => {
    if (!draftComplete && isYourTurn && timeLeft > 0) {
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            handleAutoPick();
            return 60; // Reset timer
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isYourTurn, timeLeft, draftComplete]);

  const fetchDraftData = async () => {
    try {
      setLoading(true);
      
      // Set mock data for now
      setPlayers(mockPlayers);
      setTeams(mockTeams);
      setCurrentTeam(mockTeams[0]);
      setIsYourTurn(true);
      
      // Initialize draft board
      const totalRounds = 15;
      const totalTeams = mockTeams.length;
      const board: DraftPick[] = [];
      
      for (let round = 1; round <= totalRounds; round++) {
        for (let teamIndex = 0; teamIndex < totalTeams; teamIndex++) {
          const isSnakeReverse = round % 2 === 0;
          const pickOrder = isSnakeReverse ? totalTeams - teamIndex - 1 : teamIndex;
          const team = mockTeams[pickOrder];
          
          board.push({
            round,
            pick: (round - 1) * totalTeams + teamIndex + 1,
            teamId: team.id,
            teamName: team.teamName
          });
        }
      }
      
      setDraftBoard(board);
      
    } catch (error) {
      console.error('Error fetching draft data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load draft data. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDraftPlayer = (player: Player) => {
    if (!isYourTurn || draftComplete) return;
    
    // Update player status
    setPlayers(prev => prev.map(p => 
      p.id === player.id 
        ? { ...p, status: 'drafted', draftedBy: currentTeam?.teamName }
        : p
    ));
    
    // Add to roster
    setYourRoster(prev => [...prev, player]);
    
    // Update draft board
    const currentPickIndex = (currentRound - 1) * teams.length + (currentPick - 1);
    setDraftBoard(prev => prev.map((pick, index) => 
      index === currentPickIndex 
        ? { ...pick, playerId: player.id, player }
        : pick
    ));
    
    // Remove drafted player from ALL queues (including your own)
    setYourQueue(prev => prev.filter(p => p.id !== player.id));
    // Note: In a real implementation, you'd also remove from other users' queues via database/realtime updates
    
    // Add system message with queue context
    const wasFromQueue = yourQueue.some(p => p.id === player.id);
    if (wasFromQueue) {
      addSystemMessage(`${currentTeam?.ownerName} drafted ${player.name} (from queue)`);
    } else {
      addSystemMessage(`${currentTeam?.ownerName} drafted ${player.name}`);
    }
    
    // Move to next pick
    advanceDraft();
  };

  const handleAutoPick = () => {
    let playerToPick: Player | null = null;
    let pickReason = '';
    
    // First try queue - find the first available player from the queue
    const availableQueuePlayers = yourQueue.filter(queuePlayer => 
      players.find(p => p.id === queuePlayer.id && p.status === 'available')
    );
    
    if (availableQueuePlayers.length > 0) {
      playerToPick = players.find(p => p.id === availableQueuePlayers[0].id) || null;
      pickReason = 'from queue';
    } else {
      // Fallback: Pick top available player by points
      const availablePlayers = players.filter(p => p.status === 'available');
      if (availablePlayers.length > 0) {
        playerToPick = availablePlayers[0];
        pickReason = yourQueue.length > 0 ? 'queue empty - top available' : 'top available';
      }
    }
    
    if (playerToPick) {
      handleDraftPlayer(playerToPick);
      addSystemMessage(`⏰ Auto-picked ${playerToPick.name} for ${currentTeam?.teamName} (${pickReason})`);
      
      // Show toast notification for your own auto-picks
      if (isYourTurn) {
        toast({
          title: 'Auto-Pick Executed',
          description: `${playerToPick.name} was automatically drafted ${pickReason}`,
          variant: 'default',
        });
      }
    }
  };

  const advanceDraft = () => {
    const totalTeams = teams.length;
    let nextPick = currentPick + 1;
    let nextRound = currentRound;
    
    if (nextPick > totalTeams) {
      nextPick = 1;
      nextRound += 1;
    }
    
    if (nextRound > 15) { // 15 rounds total
      setDraftComplete(true);
      addSystemMessage('Draft Complete — Season Starts Soon!');
      return;
    }
    
    setCurrentPick(nextPick);
    setCurrentRound(nextRound);
    
    // Determine next team (snake draft logic)
    const isSnakeReverse = nextRound % 2 === 0;
    const teamIndex = isSnakeReverse ? totalTeams - nextPick : nextPick - 1;
    const nextTeam = teams[teamIndex];
    
    setCurrentTeam(nextTeam);
    setIsYourTurn(nextTeam?.isYou || false);
    setTimeLeft(60);
  };

  const addToWatchlist = (player: Player) => {
    if (watchlist.find(p => p.id === player.id)) {
      setWatchlist(prev => prev.filter(p => p.id !== player.id));
    } else {
      setWatchlist(prev => [...prev, player]);
    }
  };

  const addToQueue = (player: Player) => {
    if (!yourQueue.find(p => p.id === player.id)) {
      setYourQueue(prev => [...prev, player]);
      toast({
        title: 'Added to Queue',
        description: `${player.name} added to your draft queue`,
      });
    }
  };

  const removeFromQueue = (playerId: string) => {
    setYourQueue(prev => prev.filter(p => p.id !== playerId));
  };

  const reorderQueue = (fromIndex: number, toIndex: number) => {
    setYourQueue(prev => {
      const newQueue = [...prev];
      const [removed] = newQueue.splice(fromIndex, 1);
      newQueue.splice(toIndex, 0, removed);
      return newQueue;
    });
  };

  const addSystemMessage = (message: string) => {
    const newMessage: ChatMessage = {
      id: Date.now().toString(),
      userId: 'system',
      username: 'System',
      message,
      timestamp: new Date().toISOString(),
      isSystem: true
    };
    setChatMessages(prev => [...prev, newMessage]);
  };

  const filteredPlayers = players.filter(player => {
    const matchesSearch = player.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         player.team.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPosition = positionFilter === 'all' || player.position === positionFilter;
    const matchesTeam = teamFilter === 'all' || player.team === teamFilter;
    const isAvailable = player.status === 'available';
    
    return matchesSearch && matchesPosition && matchesTeam && isAvailable;
  });

  const sortedPlayers = [...filteredPlayers].sort((a, b) => {
    switch (sortBy) {
      case 'points':
        return (b.stats?.points || 0) - (a.stats?.points || 0);
      case 'goals':
        return (b.stats?.goals || 0) - (a.stats?.goals || 0);
      case 'assists':
        return (b.stats?.assists || 0) - (a.stats?.assists || 0);
      case 'name':
        return a.name.localeCompare(b.name);
      default:
        return 0;
    }
  });

  const getTimerColor = () => {
    if (timeLeft <= 10) return 'text-red-500';
    if (timeLeft <= 30) return 'text-yellow-500';
    return 'text-green-500';
  };

  const getProgressValue = () => {
    return ((60 - timeLeft) / 60) * 100;
  };

  if (loading) {
    return (
      <SidebarProvider>
        <AppSidebar />
        <SidebarInset>
          <AppHeader />
          <div className="flex-1 overflow-auto">
            <div className="container mx-auto px-4 py-6">
              <div className="text-center">
                <Shuffle className="h-12 w-12 mx-auto text-primary animate-spin mb-4" />
                <p className="text-muted-foreground">Loading draft room...</p>
              </div>
            </div>
          </div>
        </SidebarInset>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <AppHeader />
        <div className="flex-1 overflow-auto">
          <div className="container mx-auto px-4 py-6 space-y-6">
            {/* Top Draft Banner */}
            <Card className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-2">
                      <div className="flex items-center gap-4">
                        <h1 className="text-3xl font-bold font-mono">
                          Round {currentRound} / Pick {currentPick}
                        </h1>
                        <Badge variant="outline" className="gap-2">
                          <RefreshCw className="h-4 w-4" />
                          Snake Draft
                        </Badge>
                      </div>
                      <p className="text-lg text-muted-foreground">
                        <span className="font-medium">{currentTeam?.teamName}</span> is on the clock
                      </p>
                    </div>
                    
                    <div className="text-right space-y-2">
                      <div className={`text-4xl font-mono font-bold ${getTimerColor()}`}>
                        {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {isYourTurn ? 'Your Turn!' : 'Waiting...'}
                      </div>
                    </div>
                  </div>
                  
                  <Progress 
                    value={getProgressValue()} 
                    className="h-2"
                  />
                </div>
              </CardContent>
            </Card>

            {draftComplete && (
              <Card className="bg-gradient-to-r from-green-500/10 to-green-600/5 border-green-500/20">
                <CardContent className="pt-6">
                  <div className="text-center space-y-4">
                    <Trophy className="h-12 w-12 mx-auto text-green-500" />
                    <h2 className="text-2xl font-bold">Draft Complete — Season Starts Soon!</h2>
                    <div className="flex gap-4 justify-center">
                      <Button onClick={() => navigate(`/league/${id}/draft-results`)}>
                        View Draft Results
                      </Button>
                      <Button variant="outline">
                        <Download className="h-4 w-4 mr-2" />
                        Download CSV
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Three Column Layout */}
            <div className="grid grid-cols-12 gap-6 h-[800px]">
              {/* Left Column - Player Pool */}
              <div className="col-span-4 space-y-4">
                <Card className="h-full flex flex-col">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="h-5 w-5" />
                      Player Pool
                    </CardTitle>
                    
                    {/* Search and Filters */}
                    <div className="space-y-3">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Search players..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="pl-10"
                        />
                      </div>
                      
                      <div className="flex gap-2">
                        <Select value={positionFilter} onValueChange={setPositionFilter}>
                          <SelectTrigger className="flex-1">
                            <SelectValue placeholder="Position" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Positions</SelectItem>
                            <SelectItem value="GK">Goalkeeper</SelectItem>
                            <SelectItem value="DEF">Defender</SelectItem>
                            <SelectItem value="MID">Midfielder</SelectItem>
                            <SelectItem value="FWD">Forward</SelectItem>
                          </SelectContent>
                        </Select>
                        
                        <Select value={sortBy} onValueChange={setSortBy}>
                          <SelectTrigger className="flex-1">
                            <SelectValue placeholder="Sort by" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="points">Most Points</SelectItem>
                            <SelectItem value="goals">Most Goals</SelectItem>
                            <SelectItem value="assists">Most Assists</SelectItem>
                            <SelectItem value="name">Name A-Z</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="flex-1 overflow-auto p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Player</TableHead>
                          <TableHead>Stats</TableHead>
                          <TableHead>Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sortedPlayers.map((player) => (
                          <TableRow key={player.id} className="hover:bg-muted/50">
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <Avatar className="h-8 w-8">
                                  <AvatarImage src={player.photo_url} />
                                  <AvatarFallback>{player.name.slice(0, 2)}</AvatarFallback>
                                </Avatar>
                                <div>
                                  <div className="font-medium">{player.name}</div>
                                  <div className="text-sm text-muted-foreground">
                                    {player.team} • {player.position}
                                  </div>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm space-y-1">
                                <div>Goals: {player.stats?.goals}</div>
                                <div>Assists: {player.stats?.assists}</div>
                                <div className="font-medium">Pts: {player.stats?.points}</div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Button
                                  size="sm"
                                  onClick={() => handleDraftPlayer(player)}
                                  disabled={!isYourTurn || draftComplete}
                                  className="w-full"
                                >
                                  Draft
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => addToQueue(player)}
                                  disabled={yourQueue.find(p => p.id === player.id) !== undefined}
                                  title="Add to Queue"
                                >
                                  <Plus className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => addToWatchlist(player)}
                                >
                                  <Star className={`h-4 w-4 ${watchlist.find(p => p.id === player.id) ? 'fill-current text-yellow-500' : ''}`} />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </div>

              {/* Center Column - Draft Board */}
              <div className="col-span-5 space-y-4">
                <Card className="h-full flex flex-col">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2">
                        <Target className="h-5 w-5" />
                        Draft Board
                      </CardTitle>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setBoardView(boardView === 'compact' ? 'expanded' : 'compact')}
                        >
                          {boardView === 'compact' ? 'Expand' : 'Compact'}
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="flex-1 overflow-auto p-4">
                    <div className={`space-y-${boardView === 'compact' ? '1' : '2'}`}>
                      {(boardView === 'compact' ? [1, 2, 3, 4, 5, 6, 7, 8] : [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15]).map(round => (
                        <div key={round} className="flex items-center gap-2">
                          <div className={`${boardView === 'compact' ? 'w-6 text-xs' : 'w-8 text-sm'} font-medium text-muted-foreground`}>R{round}</div>
                          <div className="flex gap-1 flex-1">
                            {teams.map((team, teamIndex) => {
                              const isSnakeReverse = round % 2 === 0;
                              const pickOrder = isSnakeReverse ? teams.length - teamIndex - 1 : teamIndex;
                              const actualTeam = teams[pickOrder];
                              const pick = draftBoard.find(p => p.round === round && p.teamId === actualTeam.id);
                              const isCurrent = round === currentRound && pick?.pick === currentPick;
                              
                              return (
                                <div
                                  key={actualTeam.id}
                                  className={`flex-1 ${
                                    boardView === 'compact' ? 'min-h-[40px]' : 'min-h-[60px]'
                                  } border rounded ${
                                    boardView === 'compact' ? 'p-1' : 'p-2'
                                  } ${
                                    boardView === 'compact' ? 'text-xs' : 'text-xs'
                                  } ${
                                    isCurrent 
                                      ? 'border-green-500 bg-green-500/10 animate-pulse' 
                                      : actualTeam.isYou 
                                        ? 'border-primary bg-primary/5' 
                                        : 'border-border'
                                  }`}
                                >
                                  {pick?.player ? (
                                    <div className={boardView === 'compact' ? 'space-y-0' : 'space-y-1'}>
                                      <div className={`font-medium truncate ${
                                        boardView === 'compact' ? 'text-xs leading-tight' : ''
                                      }`}>
                                        {boardView === 'compact' 
                                          ? pick.player.name.split(' ')[0] 
                                          : pick.player.name
                                        }
                                      </div>
                                      {boardView === 'expanded' && (
                                        <div className="text-muted-foreground">{pick.player.position}</div>
                                      )}
                                      {boardView === 'compact' && (
                                        <div className="text-muted-foreground text-xs">{pick.player.position}</div>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="text-muted-foreground">
                                      {boardView === 'compact' 
                                        ? (isCurrent ? 'Clock' : 'Pending')
                                        : (isCurrent ? 'On Clock' : 'Pick Pending')
                                      }
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Right Column - Your Team + Extras */}
              <div className="col-span-3 space-y-4">
                {/* Your Roster */}
                <Card className="flex-1">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Trophy className="h-5 w-5" />
                      Your Roster ({yourRoster.length}/21)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="text-sm text-muted-foreground">Starters (11)</div>
                    <div className="grid grid-cols-1 gap-2">
                      {Array.from({ length: 11 }, (_, i) => {
                        const player = yourRoster[i];
                        return (
                          <div key={i} className="border rounded p-2 text-xs">
                            {player ? (
                              <div className="flex items-center gap-2">
                                <Avatar className="h-6 w-6">
                                  <AvatarFallback className="text-xs">{player.name.slice(0, 2)}</AvatarFallback>
                                </Avatar>
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium truncate">{player.name}</div>
                                  <div className="text-muted-foreground">{player.position}</div>
                                </div>
                              </div>
                            ) : (
                              <div className="text-muted-foreground text-center py-2">Pick Pending</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    
                    <div className="text-sm text-muted-foreground">Bench (10)</div>
                    <div className="grid grid-cols-1 gap-2">
                      {Array.from({ length: 10 }, (_, i) => {
                        const player = yourRoster[i + 11];
                        return (
                          <div key={i} className="border rounded p-2 text-xs">
                            {player ? (
                              <div className="flex items-center gap-2">
                                <Avatar className="h-6 w-6">
                                  <AvatarFallback className="text-xs">{player.name.slice(0, 2)}</AvatarFallback>
                                </Avatar>
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium truncate">{player.name}</div>
                                  <div className="text-muted-foreground">{player.position}</div>
                                </div>
                              </div>
                            ) : (
                              <div className="text-muted-foreground text-center py-2">Pick Pending</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* Your Queue */}
                <Card className="h-80">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-sm">
                      <Target className="h-4 w-4" />
                      Your Queue ({yourQueue.length})
                    </CardTitle>
                    <p className="text-xs text-muted-foreground">
                      Auto-pick priority when your time runs out
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {yourQueue.length === 0 ? (
                      <div className="text-center py-8 text-xs text-muted-foreground">
                        <Target className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p>No players in queue</p>
                        <p className="mt-1">Add players from the left to set auto-pick priority</p>
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-56 overflow-y-auto">
                        {yourQueue.map((player, index) => {
                          const isDrafted = players.find(p => p.id === player.id)?.status === 'drafted';
                          return (
                            <div 
                              key={player.id} 
                              className={`flex items-center justify-between p-2 border rounded text-xs ${
                                isDrafted ? 'opacity-50 bg-muted/30' : 'bg-background'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                                  isDrafted ? 'bg-muted text-muted-foreground' : 'bg-primary text-primary-foreground'
                                }`}>
                                  {index + 1}
                                </div>
                                <Avatar className="h-6 w-6">
                                  <AvatarFallback className="text-xs">{player.name.slice(0, 2)}</AvatarFallback>
                                </Avatar>
                                <div className="flex-1">
                                  <div className={`font-medium truncate ${isDrafted ? 'line-through' : ''}`}>
                                    {player.name}
                                  </div>
                                  <div className="text-muted-foreground">
                                    {player.position} • {player.stats?.points || 0} pts
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                {isDrafted && (
                                  <Badge variant="outline" className="text-xs">Taken</Badge>
                                )}
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  className="h-6 w-6 p-0"
                                  onClick={() => removeFromQueue(player.id)}
                                >
                                  <XCircle className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    
                    {yourQueue.length > 0 && (
                      <div className="text-xs text-muted-foreground bg-muted/30 p-2 rounded">
                        💡 Tip: Drag to reorder priority. Unavailable players will be skipped.
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Draft Chat */}
                <Card className="h-60">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2 text-sm">
                        <MessageCircle className="h-4 w-4" />
                        Draft Chat
                      </CardTitle>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setChatCollapsed(!chatCollapsed)}
                      >
                        {chatCollapsed ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </Button>
                    </div>
                  </CardHeader>
                  {!chatCollapsed && (
                    <CardContent className="flex-1 overflow-auto p-2">
                      <div className="space-y-2">
                        {chatMessages.map((msg) => (
                          <div
                            key={msg.id}
                            className={`text-xs p-2 rounded ${
                              msg.isSystem 
                                ? 'bg-muted/50 text-muted-foreground italic' 
                                : 'bg-background border'
                            }`}
                          >
                            {!msg.isSystem && (
                              <div className="font-medium text-primary mb-1">{msg.username}</div>
                            )}
                            <div>{msg.message}</div>
                            <div className="text-muted-foreground text-xs mt-1">
                              {new Date(msg.timestamp).toLocaleTimeString()}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  )}
                </Card>
              </div>
            </div>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
};

export default DraftRoom;